import React, { useEffect, useMemo, useState } from 'react';
import { DingtalkIcon, FeishuIcon, SSOIcon, WeWorkIcon } from '../../icons-oauth';
import type { LoginPageLogoLocation, LoginPageProps } from './types';

const LOGIN_LOGO_POSITIONS: LoginPageLogoLocation[] = ['top', 'middle', 'bottom'];

const normalizeCssValue = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

const createBackgroundImage = (gradient?: string, imageUrl?: string) => {
  const resolvedGradient = normalizeCssValue(gradient);
  const resolvedImageUrl = normalizeCssValue(imageUrl);

  return [resolvedGradient, resolvedImageUrl ? `url(${JSON.stringify(resolvedImageUrl)})` : undefined]
    .filter(Boolean)
    .join(', ');
};

const normalizeLogoPosition = (position?: LoginPageLogoLocation): LoginPageLogoLocation =>
  position && LOGIN_LOGO_POSITIONS.includes(position) ? position : 'bottom';

const normalizeLogoScale = (scale?: number) =>
  typeof scale === 'number' && Number.isFinite(scale) && scale > 0 ? scale : undefined;

const resolveLogoUrls = (logo?: LoginPageProps['logo']) => {
  const url = normalizeCssValue(logo?.url);
  const lightUrl = normalizeCssValue(logo?.lightUrl) || url;
  const darkUrl = normalizeCssValue(logo?.darkUrl) || url || lightUrl;

  return { lightUrl, darkUrl };
};

const normalizeProviderType = (value?: string) =>
  normalizeCssValue(value)
    ?.toLowerCase()
    .replace(/^oauth[-_:]/, '')
    .replace(/[\s_]+/g, '-');

const getDefaultExternalMethodIcon = (method: NonNullable<LoginPageProps['externalMethods']>[number]) => {
  const providerType = normalizeProviderType(method.providerType) ?? normalizeProviderType(method.id);

  switch (providerType) {
    case 'dingtalk':
    case 'ding-talk':
    case 'dingding':
      return <DingtalkIcon aria-hidden="true" />;
    case 'feishu':
    case 'lark':
      return <FeishuIcon alt="" aria-hidden="true" />;
    case 'wework':
    case 'wecom':
    case 'wechat-work':
    case 'wechatwork':
      return <WeWorkIcon aria-hidden="true" />;
    case 'oidc':
    case 'sso':
    case 'saml':
    case 'saml-sso':
      return <SSOIcon aria-hidden="true" />;
    default:
      return null;
  }
};

export function LoginPage({
  className,
  style,
  background,
  logo,
  toolbar,
  title,
  backAction,
  methods,
  activeMethodId,
  defaultMethodId,
  onMethodChange,
  externalLabel,
  externalMethods = [],
  externalPanel,
  callout,
}: LoginPageProps) {
  const availableMethods = useMemo(() => methods.filter((method) => !method.hidden), [methods]);
  const fallbackMethodId = defaultMethodId ?? availableMethods[0]?.id;
  const [internalMethodId, setInternalMethodId] = useState<string | undefined>(fallbackMethodId);
  const currentMethodId = activeMethodId ?? internalMethodId;
  const activeMethod = availableMethods.find((method) => method.id === currentMethodId) ?? availableMethods[0];
  const hasTabs = availableMethods.length > 1;
  const showAuthMethods = !externalPanel;
  const backgroundImage = createBackgroundImage(background?.gradient, background?.imageUrl);
  const backgroundMode = backgroundImage ? 'custom' : 'default';
  const logoPosition = normalizeLogoPosition(logo?.position);
  const logoScale = normalizeLogoScale(logo?.scale);
  const { lightUrl: logoLightUrl, darkUrl: logoDarkUrl } = resolveLogoUrls(logo);
  const hasLogo = Boolean(logoLightUrl || logoDarkUrl);
  const usesSeparateModeLogos = Boolean(logoLightUrl && logoDarkUrl && logoLightUrl !== logoDarkUrl);

  useEffect(() => {
    if (!availableMethods.length) {
      setInternalMethodId(undefined);
      return;
    }

    if (!currentMethodId || !availableMethods.some((method) => method.id === currentMethodId)) {
      setInternalMethodId(fallbackMethodId ?? availableMethods[0]?.id);
    }
  }, [availableMethods, currentMethodId, fallbackMethodId]);

  const handleMethodChange = (methodId: string) => {
    if (activeMethodId === undefined) {
      setInternalMethodId(methodId);
    }
    onMethodChange?.(methodId);
  };

  const renderExternalMethod = (method: NonNullable<LoginPageProps['externalMethods']>[number]) => {
    const icon = method.icon === undefined ? getDefaultExternalMethodIcon(method) : method.icon;
    const button = (
      <button
        type="button"
        className="login-page__oauth-button"
        data-login-part="external-button"
        data-login-method-id={method.id}
        onClick={method.onClick}
        disabled={method.disabled}
      >
        {icon ? (
          <span className="login-page__oauth-icon" data-login-part="external-icon">
            {icon}
          </span>
        ) : null}
        <span>{method.label}</span>
      </button>
    );

    return <React.Fragment key={method.id}>{method.render ? method.render(button) : button}</React.Fragment>;
  };

  return (
    <main
      className={['login-page', className].filter(Boolean).join(' ')}
      data-login-page
      data-login-part="root"
      data-login-background={backgroundMode}
      style={style}
    >
      <div
        className="login-page__background"
        data-login-part="background"
        data-login-background={backgroundMode}
        style={backgroundImage ? { backgroundImage } : undefined}
        aria-hidden="true"
      />

      {toolbar ? (
        <div className="login-page__toolbar" data-login-part="toolbar">
          {toolbar}
        </div>
      ) : null}

      {hasLogo ? (
        <div
          className={`login-page__logo login-page__logo--${logoPosition}`}
          data-login-part="logo"
          data-logo-position={logoPosition}
        >
          {usesSeparateModeLogos ? (
            <>
              <img
                className="login-page__logo-image login-page__logo-image--light"
                data-login-part="logo-image"
                data-login-logo-mode="light"
                src={logoLightUrl}
                alt={logo?.alt ?? ''}
                style={logoScale ? { transform: `scale(${logoScale})` } : undefined}
              />
              <img
                className="login-page__logo-image login-page__logo-image--dark"
                data-login-part="logo-image"
                data-login-logo-mode="dark"
                src={logoDarkUrl}
                alt={logo?.alt ?? ''}
                style={logoScale ? { transform: `scale(${logoScale})` } : undefined}
              />
            </>
          ) : (
            <img
              className="login-page__logo-image"
              data-login-part="logo-image"
              src={logoLightUrl || logoDarkUrl}
              alt={logo?.alt ?? ''}
              style={logoScale ? { transform: `scale(${logoScale})` } : undefined}
            />
          )}
        </div>
      ) : null}

      <section
        className="login-page__form"
        data-login-part="panel"
        aria-label={typeof title === 'string' ? title : undefined}
      >
        {backAction ? (
          <button type="button" className="login-page__back" data-login-part="back" onClick={backAction.onClick}>
            {backAction.icon ? (
              <span className="login-page__back-icon" data-login-part="back-icon">
                {backAction.icon}
              </span>
            ) : null}
            <span>{backAction.label}</span>
          </button>
        ) : null}

        {title ? (
          <h1 className="login-page__title" data-login-part="title">
            {title}
          </h1>
        ) : null}

        {showAuthMethods && hasTabs ? (
          <div className="login-page__tabs" data-login-part="tabs" role="tablist">
            {availableMethods.map((method) => {
              const selected = method.id === activeMethod?.id;
              return (
                <button
                  key={method.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`login-page__tab ${selected ? 'login-page__tab--active' : ''}`}
                  data-login-part="tab"
                  data-login-method-id={method.id}
                  data-active={selected ? 'true' : 'false'}
                  onClick={() => handleMethodChange(method.id)}
                >
                  {method.icon ? (
                    <span className="login-page__tab-icon" data-login-part="tab-icon">
                      {method.icon}
                    </span>
                  ) : null}
                  <span className="login-page__tab-label">{method.label}</span>
                </button>
              );
            })}
          </div>
        ) : null}

        {externalPanel ? (
          <div className="login-page__external-panel" data-login-part="external-panel">
            {externalPanel.title ? (
              <div className="login-page__external-panel-title" data-login-part="external-panel-title">
                {externalPanel.title}
              </div>
            ) : null}
            {externalPanel.description ? (
              <p className="login-page__external-panel-description" data-login-part="external-panel-description">
                {externalPanel.description}
              </p>
            ) : null}
            <div className="login-page__external-panel-content" data-login-part="external-panel-content">
              {externalPanel.content}
            </div>
            {externalPanel.footer || externalPanel.backAction ? (
              <div className="login-page__external-panel-footer" data-login-part="external-panel-footer">
                {externalPanel.footer}
                {externalPanel.backAction ? (
                  <button
                    type="button"
                    className="login-page__external-panel-back"
                    data-login-part="external-panel-back"
                    onClick={externalPanel.backAction.onClick}
                  >
                    {externalPanel.backAction.label}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : showAuthMethods && activeMethod ? (
          <form
            className="login-page__form-fields"
            data-login-part="form"
            data-login-method-id={activeMethod.id}
            onSubmit={activeMethod.onSubmit}
          >
            {activeMethod.fields.map((field) => (
              <div className="login-page__field" data-login-part="field" data-login-field-name={field.name} key={field.name}>
                <input
                  id={field.id}
                  name={field.name}
                  type={field.type ?? 'text'}
                  value={field.value}
                  placeholder={field.placeholder}
                  autoComplete={field.autoComplete}
                  inputMode={field.inputMode}
                  maxLength={field.maxLength}
                  pattern={field.pattern}
                  disabled={activeMethod.disabled || activeMethod.isLoading || field.disabled}
                  required={field.required}
                  className="login-page__input"
                  data-login-part="input"
                  onChange={(event) => field.onChange(event.target.value)}
                />
              </div>
            ))}

            {activeMethod.checkbox ? (
              <label className="login-page__remember" data-login-part="checkbox-label" htmlFor={activeMethod.checkbox.id}>
                <input
                  id={activeMethod.checkbox.id}
                  type="checkbox"
                  className="login-page__checkbox"
                  data-login-part="checkbox"
                  checked={activeMethod.checkbox.checked}
                  disabled={activeMethod.disabled || activeMethod.isLoading || activeMethod.checkbox.disabled}
                  onChange={(event) => activeMethod.checkbox?.onChange(event.target.checked)}
                />
                <span>{activeMethod.checkbox.label}</span>
              </label>
            ) : null}

            {activeMethod.footer || activeMethod.footerAction ? (
              <div className="login-page__method-footer" data-login-part="method-footer">
                {activeMethod.footer}
                {activeMethod.footerAction ? (
                  <button
                    type="button"
                    className="login-page__method-footer-action"
                    data-login-part="method-footer-action"
                    data-login-method-id={activeMethod.id}
                    onClick={activeMethod.footerAction.onClick}
                    disabled={activeMethod.disabled || activeMethod.isLoading || activeMethod.footerAction.disabled}
                  >
                    {activeMethod.footerAction.label}
                  </button>
                ) : null}
              </div>
            ) : null}

            <button
              type="submit"
              className="login-page__submit"
              data-login-part="submit-button"
              disabled={activeMethod.disabled || activeMethod.isLoading}
            >
              {activeMethod.isLoading && activeMethod.loadingLabel ? activeMethod.loadingLabel : activeMethod.submitLabel}
            </button>
          </form>
        ) : null}

        {callout ? (
          <div
            className={`login-page__callout login-page__callout--${callout.tone ?? 'info'}`}
            data-login-part="callout"
            data-tone={callout.tone ?? 'info'}
          >
            <div className="login-page__callout-title" data-login-part="callout-title">
              {callout.title}
            </div>
            {callout.description ? (
              <p className="login-page__callout-description" data-login-part="callout-description">
                {callout.description}
              </p>
            ) : null}
            {callout.detail ? (
              <p className="login-page__callout-detail" data-login-part="callout-detail">
                {callout.detail}
              </p>
            ) : null}
            {callout.action ? (
              <button
                type="button"
                className="login-page__callout-action"
                data-login-part="callout-action"
                onClick={callout.action.onClick}
                disabled={callout.action.disabled}
              >
                {callout.action.label}
              </button>
            ) : null}
          </div>
        ) : null}

        {!externalPanel && externalMethods.length ? (
          <div className="login-page__oauth-section" data-login-part="external-methods">
            {externalLabel ? (
              <div className="login-page__oauth-label" data-login-part="external-label">
                {externalLabel}
              </div>
            ) : null}
            <div className="login-page__oauth-list" data-login-part="external-list">
              {externalMethods.map(renderExternalMethod)}
            </div>
          </div>
        ) : null}
      </section>
    </main>
  );
}
